import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  writeBatch,
  getDoc,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const BalanceCorrectionTool = () => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [selectedDiscrepancies, setSelectedDiscrepancies] = useState(new Set());
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [userFilter, setUserFilter] = useState("");
  const [progress, setProgress] = useState(0);
  const [ignoreBeforeDate, setIgnoreBeforeDate] = useState("2024-12-01"); // Default: ignore transactions before Dec 2024
  const [showDetails, setShowDetails] = useState({});
  const [analysisSummary, setAnalysisSummary] = useState(null);

  const analyzeTransactions = async () => {
    setAnalyzing(true);
    setDiscrepancies([]);
    setProgress(0);

    try {
      // Get all users
      const usersSnapshot = await getDocs(collection(DATABASE, "users"));
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setProgress(10);

      // Get all transactions
      const transactionsSnapshot = await getDocs(
        query(collection(DATABASE, "transection_requests"), orderBy("date", "desc"))
      );
      const transactions = transactionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setProgress(30);

      // Get all ledgers
      const ledgersSnapshot = await getDocs(
        query(collection(DATABASE, "ledgers"), orderBy("date", "desc"))
      );
      const ledgers = ledgersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setProgress(50);

      const foundDiscrepancies = [];

      // Analyze each user
      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        const userId = user.id;

        // Filter transactions for this user
        const userTransactions = transactions.filter(
          (txn) => txn.user_id === userId
        );

        // Filter ledgers for this user
        const userLedgers = ledgers.filter((ledger) => ledger.user_id === userId);

        // Calculate expected balance from ledgers (chronologically, only after ignore date)
        const ignoreDate = new Date(ignoreBeforeDate);
        
        // Get current balance from user document
        const currentBalance = parseFloat(user.net_balance || 0);
        
        // Sort ledgers chronologically (oldest first) to find starting balance
        const sortedLedgers = [...userLedgers].sort((a, b) => {
          const dateA = a.date?.toDate?.() || new Date(a.date?.seconds * 1000) || new Date(0);
          const dateB = b.date?.toDate?.() || new Date(b.date?.seconds * 1000) || new Date(0);
          return dateA - dateB; // Sort oldest first (ascending)
        });

        // Find the last ledger before ignore date to get the starting balance
        // Use the current_balance from that ledger as the starting point
        let startingBalance = null;
        let lastLedgerBeforeDate = null;
        
        for (const ledger of sortedLedgers) {
          const ledgerDate = ledger.date?.toDate?.() || new Date(ledger.date?.seconds * 1000) || new Date(0);
          if (ledgerDate < ignoreDate) {
            // Use the current_balance from the last ledger before the ignore date
            const ledgerBalance = parseFloat(ledger.current_balance || ledger.balance || 0);
            if (ledgerBalance > 0 || startingBalance === null) {
              startingBalance = ledgerBalance;
              lastLedgerBeforeDate = ledger;
            }
          }
        }
        
        // If no ledger before ignore date, try to calculate by reversing from current balance
        if (startingBalance === null) {
          // No ledgers before ignore date, calculate by reversing all transactions after ignore date
          const sortedLedgersDesc = [...userLedgers].sort((a, b) => {
            const dateA = a.date?.toDate?.() || new Date(a.date?.seconds * 1000) || new Date(0);
            const dateB = b.date?.toDate?.() || new Date(b.date?.seconds * 1000) || new Date(0);
            return dateB - dateA; // Sort newest first (descending)
          });
          
          startingBalance = currentBalance;
          for (const ledger of sortedLedgersDesc) {
            const ledgerDate = ledger.date?.toDate?.() || new Date(ledger.date?.seconds * 1000) || new Date(0);
            if (ledgerDate >= ignoreDate) {
              const amount = parseFloat(ledger.new_txn_amount || 0);
              if (ledger.transection_type === 0) {
                // Deposit - reverse by subtracting
                startingBalance -= amount;
              } else if (ledger.transection_type === 2) {
                // Withdrawal - reverse by adding back
                startingBalance += amount;
              }
            }
          }
        }

        // Calculate expected balance by applying all transactions after ignore date
        let expectedBalance = startingBalance || 0;
        let ledgersAfterDate = 0;

        // Apply all transactions after ignore date
        for (const ledger of sortedLedgers) {
          const ledgerDate = ledger.date?.toDate?.() || new Date(ledger.date?.seconds * 1000) || new Date(0);
          if (ledgerDate >= ignoreDate) {
            const amount = parseFloat(ledger.new_txn_amount || 0);
            if (ledger.transection_type === 0) {
              // Deposit
              expectedBalance += amount;
            } else if (ledger.transection_type === 2) {
              // Withdrawal
              expectedBalance -= amount;
            }
            ledgersAfterDate++;
          }
        }

        // Only check for balance mismatch if we have ledgers after the ignore date
        // This avoids false positives from old data
        // The expected balance should equal current balance if everything is correct
        if (ledgersAfterDate > 0 && Math.abs(expectedBalance - currentBalance) > 0.01) {
          foundDiscrepancies.push({
            userId: userId,
            userEmail: user.email || "Unknown",
            userName: user.full_name || "Unknown",
            currentBalance: currentBalance,
            expectedBalance: expectedBalance,
            startingBalance: startingBalance || 0, // This is the balance at ignore date
            difference: expectedBalance - currentBalance,
            transactionCount: userTransactions.filter(txn => {
              const txnDate = txn.date?.toDate?.() || txn.request_approval_time?.toDate?.() || new Date(txn.date?.seconds * 1000) || new Date();
              return txnDate >= ignoreDate;
            }).length,
            ledgerCount: userLedgers.length,
            ledgersAfterDate: ledgersAfterDate,
            type: "balance_mismatch",
          });
        }

        // Check for transactions without ledgers (with date filter)
        // Note: ignoreDate is already declared above, reusing it
        
        for (const txn of userTransactions) {
          const txnDate = txn.date?.toDate?.() || txn.request_approval_time?.toDate?.() || new Date(txn.date?.seconds * 1000) || new Date();
          
          // Skip transactions before the ignore date
          if (txnDate < ignoreDate) {
            continue;
          }
          
          if (txn.request_status === 2 && txn.request_type === 0) {
            // Approved deposit
            const hasLedger = userLedgers.some(
              (ledger) => ledger.doc_id === txn.doc_id || ledger.doc_id === txn.id
            );
            if (!hasLedger) {
              foundDiscrepancies.push({
                userId: userId,
                userEmail: user.email || "Unknown",
                userName: user.full_name || "Unknown",
                transactionId: txn.id,
                transactionDocId: txn.doc_id,
                transactionAmount: parseFloat(txn.total_amount || 0),
                transactionType: "deposit",
                transactionDate: txnDate,
                type: "missing_ledger",
              });
            }
          }

          if (txn.request_status === 1 && txn.request_type === 1 && txn.otp_verify === true) {
            // Verified withdrawal
            const hasLedger = userLedgers.some(
              (ledger) => ledger.doc_id === txn.doc_id || ledger.doc_id === txn.id
            );
            if (!hasLedger) {
              foundDiscrepancies.push({
                userId: userId,
                userEmail: user.email || "Unknown",
                userName: user.full_name || "Unknown",
                transactionId: txn.id,
                transactionDocId: txn.doc_id,
                transactionAmount: parseFloat(txn.total_amount || 0),
                transactionType: "withdrawal",
                transactionDate: txnDate,
                type: "missing_ledger",
              });
            }
          }
        }

        setProgress(50 + (i / users.length) * 40);
      }

      setDiscrepancies(foundDiscrepancies);
      setProgress(100);

      // Calculate summary statistics
      const summary = {
        total: foundDiscrepancies.length,
        balanceMismatches: foundDiscrepancies.filter(d => d.type === "balance_mismatch").length,
        missingLedgers: foundDiscrepancies.filter(d => d.type === "missing_ledger").length,
        byUser: {},
        totalMissingLedgerAmount: foundDiscrepancies
          .filter(d => d.type === "missing_ledger")
          .reduce((sum, d) => sum + (d.transactionAmount || 0), 0),
      };

      // Group by user
      foundDiscrepancies.forEach(d => {
        if (!summary.byUser[d.userId]) {
          summary.byUser[d.userId] = {
            userName: d.userName,
            userEmail: d.userEmail,
            balanceMismatches: 0,
            missingLedgers: 0,
            missingLedgerAmount: 0,
          };
        }
        if (d.type === "balance_mismatch") {
          summary.byUser[d.userId].balanceMismatches++;
        } else if (d.type === "missing_ledger") {
          summary.byUser[d.userId].missingLedgers++;
          summary.byUser[d.userId].missingLedgerAmount += d.transactionAmount || 0;
        }
      });

      setAnalysisSummary(summary);

      Swal.fire({
        icon: "info",
        title: "Analysis Complete",
        html: `
          <div>
            <p><strong>Found ${foundDiscrepancies.length} discrepancies</strong></p>
            <p>Balance mismatches: ${summary.balanceMismatches}</p>
            <p>Missing ledgers: ${summary.missingLedgers}</p>
            <p style="margin-top: 15px; font-size: 12px; color: #666;">
              <strong>Note:</strong> Transactions before ${ignoreBeforeDate} were ignored.<br/>
              ${Object.keys(summary.byUser).length} user(s) affected.
            </p>
          </div>
        `,
        width: 500,
      });
    } catch (error) {
      console.error("Error analyzing transactions:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to analyze transactions: ${error.message}`,
      });
    } finally {
      setAnalyzing(false);
      setProgress(0);
    }
  };

  const handleSelectDiscrepancy = (index) => {
    const newSelected = new Set(selectedDiscrepancies);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedDiscrepancies(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedDiscrepancies.size === discrepancies.length) {
      setSelectedDiscrepancies(new Set());
    } else {
      setSelectedDiscrepancies(new Set(discrepancies.map((_, i) => i)));
    }
  };

  const previewCorrections = async () => {
    if (selectedDiscrepancies.size === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select at least one discrepancy to preview corrections.",
      });
      return;
    }

    setPreviewMode(true);
    const selected = Array.from(selectedDiscrepancies).map((i) => discrepancies[i]);

    const corrections = [];

    for (const disc of selected) {
      if (disc.type === "balance_mismatch") {
        corrections.push({
          type: "balance_update",
          userId: disc.userId,
          userEmail: disc.userEmail,
          userName: disc.userName,
          currentBalance: disc.currentBalance,
          newBalance: disc.expectedBalance,
          difference: disc.difference,
        });
      } else if (disc.type === "missing_ledger") {
        // Get user current balance to calculate ledger values
        const userDoc = await getDoc(doc(DATABASE, "users", disc.userId));
        const userData = userDoc.data();
        const currentBalance = parseFloat(userData?.net_balance || 0);

        let previousBalance = currentBalance;
        let newBalance = currentBalance;

        if (disc.transactionType === "deposit") {
          newBalance = currentBalance + disc.transactionAmount;
        } else if (disc.transactionType === "withdrawal") {
          previousBalance = currentBalance + disc.transactionAmount; // Balance before withdrawal
          newBalance = currentBalance;
        }

        corrections.push({
          type: "create_ledger",
          userId: disc.userId,
          userEmail: disc.userEmail,
          userName: disc.userName,
          transactionId: disc.transactionId,
          transactionDocId: disc.transactionDocId,
          transactionAmount: disc.transactionAmount,
          transactionType: disc.transactionType,
          previousBalance: previousBalance,
          newBalance: newBalance,
        });
      }
    }

    setPreviewData(corrections);
  };

  const executeCorrections = async () => {
    if (!previewData || previewData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Preview Data",
        text: "Please preview corrections first.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      html: `
        <div>
          <p><strong>You are about to correct ${previewData.length} discrepancies.</strong></p>
          <p>This will:</p>
          <ul style="text-align: left; margin: 10px 0;">
            ${previewData
              .filter((c) => c.type === "balance_update")
              .map((c) => `<li>Update ${c.userName}'s balance from $${c.currentBalance.toFixed(2)} to $${c.newBalance.toFixed(2)}</li>`)
              .join("")}
            ${previewData
              .filter((c) => c.type === "create_ledger")
              .map((c) => `<li>Create ledger for ${c.userName}'s ${c.transactionType} of $${c.transactionAmount.toFixed(2)}</li>`)
              .join("")}
          </ul>
          <p style="color: red; font-weight: bold;">This action cannot be undone!</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, execute corrections",
      cancelButtonText: "Cancel",
      input: "text",
      inputPlaceholder: "Type 'EXECUTE' to confirm",
      inputValidator: (value) => {
        if (value !== "EXECUTE") {
          return "You must type 'EXECUTE' to confirm";
        }
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      const batch = writeBatch(DATABASE);
      let updateCount = 0;

      for (let i = 0; i < previewData.length; i++) {
        const correction = previewData[i];

        if (correction.type === "balance_update") {
          const userRef = doc(DATABASE, "users", correction.userId);
          batch.update(userRef, {
            net_balance: correction.newBalance,
            balance_corrected_at: new Date(),
            balance_correction_note: `Corrected from $${correction.currentBalance.toFixed(2)} to $${correction.newBalance.toFixed(2)} on ${new Date().toISOString()}`,
          });
          updateCount++;
        } else if (correction.type === "create_ledger") {
          const ledgerRef = doc(collection(DATABASE, "ledgers"));
          batch.set(ledgerRef, {
            new_txn_amount: correction.transactionAmount,
            previous_balance: correction.previousBalance,
            current_balance: correction.newBalance,
            date: correction.transactionDate || new Date(),
            doc_id: correction.transactionDocId || correction.transactionId,
            profit_loss_percent: 0,
            transection_type: correction.transactionType === "deposit" ? 0 : 2,
            user_id: correction.userId,
            profit_loss_balance: 0,
            created_by_correction_tool: true,
            correction_date: new Date(),
          });
          updateCount++;
        }

        setProgress((i / previewData.length) * 90);
      }

      await batch.commit();
      setProgress(100);

      Swal.fire({
        icon: "success",
        title: "Corrections Applied!",
        html: `
          <div>
            <p><strong>Successfully corrected ${updateCount} discrepancies</strong></p>
            <p>Please verify the results in the database.</p>
          </div>
        `,
        width: 500,
      });

      // Refresh analysis
      await analyzeTransactions();
      setPreviewMode(false);
      setPreviewData(null);
      setSelectedDiscrepancies(new Set());
    } catch (error) {
      console.error("Error executing corrections:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to execute corrections: ${error.message}`,
      });
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const filteredDiscrepancies = userFilter
    ? discrepancies.filter(
        (d) =>
          d.userEmail.toLowerCase().includes(userFilter.toLowerCase()) ||
          d.userName.toLowerCase().includes(userFilter.toLowerCase())
      )
    : discrepancies;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Balance Correction Tool
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>⚠️ IMPORTANT:</strong> This tool analyzes transactions and ledgers to identify balance discrepancies.
          <br />
          <strong>Always preview corrections before executing.</strong> All corrections are logged for audit purposes.
        </Typography>
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Analysis Settings
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Analyzes all transactions and ledgers to find balance discrepancies and missing ledger entries.
              </Typography>
              
              <TextField
                label="Ignore transactions before date"
                type="date"
                value={ignoreBeforeDate}
                onChange={(e) => setIgnoreBeforeDate(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                helperText="Transactions before this date will be ignored (useful for old app versions)"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={analyzeTransactions}
                disabled={analyzing}
                fullWidth
              >
                {analyzing ? "Analyzing..." : "Analyze Transactions"}
              </Button>
              {analyzing && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    {progress.toFixed(0)}% complete
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Statistics
              </Typography>
              <Typography variant="h4" sx={{ my: 2 }}>
                {discrepancies.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total discrepancies found
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Balance Mismatches: ${discrepancies.filter((d) => d.type === "balance_mismatch").length}`}
                  color="error"
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label={`Missing Ledgers: ${discrepancies.filter((d) => d.type === "missing_ledger").length}`}
                  color="warning"
                  size="small"
                />
              </Box>
              {analysisSummary && (
                <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography variant="caption" color="text.secondary">
                    Total missing ledger amount: ${analysisSummary.totalMissingLedgerAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Users affected: {Object.keys(analysisSummary.byUser).length}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {discrepancies.length > 0 && (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ IMPORTANT:</strong> Found {discrepancies.length} discrepancies. 
              {analysisSummary && (
                <>
                  <br />• {analysisSummary.balanceMismatches} balance mismatches
                  <br />• {analysisSummary.missingLedgers} missing ledgers
                  <br />• {Object.keys(analysisSummary.byUser).length} user(s) affected
                  <br />• Total missing ledger amount: ${analysisSummary.totalMissingLedgerAmount.toFixed(2)}
                </>
              )}
              <br />
              <strong>Always review each discrepancy carefully before executing corrections.</strong>
              <br />
              Transactions before {ignoreBeforeDate} are being ignored.
            </Typography>
          </Alert>

          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              label="Filter by user email or name"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <Button
              variant="outlined"
              onClick={handleSelectAll}
              disabled={discrepancies.length === 0}
            >
              {selectedDiscrepancies.size === discrepancies.length
                ? "Deselect All"
                : "Select All"}
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={() => {
                // Select only balance mismatches
                const balanceMismatchIndices = discrepancies
                  .map((d, i) => d.type === "balance_mismatch" ? i : null)
                  .filter(i => i !== null);
                setSelectedDiscrepancies(new Set(balanceMismatchIndices));
              }}
              disabled={discrepancies.filter(d => d.type === "balance_mismatch").length === 0}
            >
              Select Balance Mismatches Only
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                // Select only missing ledgers
                const missingLedgerIndices = discrepancies
                  .map((d, i) => d.type === "missing_ledger" ? i : null)
                  .filter(i => i !== null);
                setSelectedDiscrepancies(new Set(missingLedgerIndices));
              }}
              disabled={discrepancies.filter(d => d.type === "missing_ledger").length === 0}
            >
              Select Missing Ledgers Only
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<VisibilityIcon />}
              onClick={previewCorrections}
              disabled={selectedDiscrepancies.size === 0 || loading}
            >
              Preview Corrections ({selectedDiscrepancies.size})
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDiscrepancies.size === discrepancies.length && discrepancies.length > 0}
                      indeterminate={
                        selectedDiscrepancies.size > 0 &&
                        selectedDiscrepancies.size < discrepancies.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>User</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Current</TableCell>
                      <TableCell>Expected</TableCell>
                      <TableCell>Difference</TableCell>
                      <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDiscrepancies.map((disc, index) => {
                  const originalIndex = discrepancies.indexOf(disc);
                  const isSelected = selectedDiscrepancies.has(originalIndex);

                  return (
                    <React.Fragment key={originalIndex}>
                      <TableRow selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectDiscrepancy(originalIndex)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={disc.type === "balance_mismatch" ? "Balance Mismatch" : "Missing Ledger"}
                            color={disc.type === "balance_mismatch" ? "error" : "warning"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {disc.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {disc.userEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {disc.type === "missing_ledger" && (
                            <Box>
                              <Typography variant="body2">
                                {disc.transactionType === "deposit" ? "Deposit" : "Withdrawal"}: ${disc.transactionAmount?.toFixed(2) || "0.00"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {dayjs(disc.transactionDate).format("MMM D, YYYY HH:mm")}
                              </Typography>
                            </Box>
                          )}
                          {disc.type === "balance_mismatch" && (
                            <Typography variant="body2">
                              {disc.transactionCount} transactions, {disc.ledgerCount} ledgers
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {disc.type === "balance_mismatch" && (
                            <Typography variant="body2" color="error">
                              ${disc.currentBalance.toFixed(2)}
                            </Typography>
                          )}
                          {disc.type === "missing_ledger" && (
                            <Typography variant="body2" color="text.secondary">
                              No ledger
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {disc.type === "balance_mismatch" && (
                            <Typography variant="body2" color="success.main">
                              ${disc.expectedBalance.toFixed(2)}
                            </Typography>
                          )}
                          {disc.type === "missing_ledger" && (
                            <Typography variant="body2" color="warning.main">
                              Needs ledger
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {disc.type === "balance_mismatch" && (
                            <Typography
                              variant="body2"
                              color={disc.difference > 0 ? "success.main" : "error"}
                              fontWeight="bold"
                            >
                              {disc.difference > 0 ? "+" : ""}${disc.difference.toFixed(2)}
                            </Typography>
                          )}
                          {disc.type === "missing_ledger" && (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View detailed information about this discrepancy">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const current = showDetails[originalIndex] || false;
                                setShowDetails({ ...showDetails, [originalIndex]: !current });
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {showDetails[originalIndex] && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ backgroundColor: "rgba(255,255,255,0.05)", py: 2 }}>
                            <Box sx={{ pl: 4 }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                Detailed Information:
                              </Typography>
                              {disc.type === "balance_mismatch" && (
                                <Box>
                                  <Typography variant="body2">
                                    • Current balance in users collection: <strong>${disc.currentBalance.toFixed(2)}</strong>
                                  </Typography>
                                  <Typography variant="body2">
                                    • Expected balance based on ledgers (after {ignoreBeforeDate}): <strong>${disc.expectedBalance.toFixed(2)}</strong>
                                  </Typography>
                                  {disc.startingBalance !== undefined && (
                                    <Typography variant="body2" color="text.secondary">
                                      • Starting balance (before {ignoreBeforeDate}): ${disc.startingBalance.toFixed(2)}
                                    </Typography>
                                  )}
                                  <Typography variant="body2">
                                    • Difference: <strong style={{ color: disc.difference > 0 ? "#4caf50" : "#f44336" }}>
                                      {disc.difference > 0 ? "+" : ""}${disc.difference.toFixed(2)}
                                    </strong>
                                  </Typography>
                                  <Typography variant="body2">
                                    • Transactions after {ignoreBeforeDate}: {disc.transactionCount}
                                  </Typography>
                                  <Typography variant="body2">
                                    • Total ledgers: {disc.ledgerCount} ({disc.ledgersAfterDate || 0} after {ignoreBeforeDate})
                                  </Typography>
                                  <Alert severity="warning" sx={{ mt: 1 }}>
                                    <Typography variant="caption">
                                      <strong>Warning:</strong> This will update the user's net_balance to match the ledger-calculated balance.
                                      <br />
                                      The calculation only considers ledgers after {ignoreBeforeDate}. Make sure all ledgers are correct before applying this correction.
                                      <br />
                                      <strong>If this user has many old transactions, verify the starting balance is correct.</strong>
                                    </Typography>
                                  </Alert>
                                </Box>
                              )}
                              {disc.type === "missing_ledger" && (
                                <Box>
                                  <Typography variant="body2">
                                    • Transaction ID: {disc.transactionId}
                                  </Typography>
                                  <Typography variant="body2">
                                    • Transaction Doc ID: {disc.transactionDocId || "N/A"}
                                  </Typography>
                                  <Typography variant="body2">
                                    • Amount: ${disc.transactionAmount.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2">
                                    • Type: {disc.transactionType === "deposit" ? "Deposit" : "Withdrawal"}
                                  </Typography>
                                  <Typography variant="body2">
                                    • Date: {dayjs(disc.transactionDate).format("MMM D, YYYY HH:mm")}
                                  </Typography>
                                  <Alert severity="info" sx={{ mt: 1 }}>
                                    <Typography variant="caption">
                                      <strong>Info:</strong> This will create a ledger entry for this transaction.
                                      The ledger will be created with the correct previous_balance and current_balance based on the transaction date.
                                    </Typography>
                                  </Alert>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewMode}
        onClose={() => setPreviewMode(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preview Corrections
          <Tooltip title="This is a preview. No changes have been made yet.">
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Preview Mode:</strong> The following corrections will be applied when you click "Execute Corrections".
                  No changes have been made yet.
                </Typography>
              </Alert>

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Current</TableCell>
                      <TableCell>New</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((correction, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={correction.type === "balance_update" ? "Balance Update" : "Create Ledger"}
                            color={correction.type === "balance_update" ? "primary" : "secondary"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {correction.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {correction.userEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {correction.type === "balance_update" && (
                            <Typography variant="body2">
                              Update balance
                            </Typography>
                          )}
                          {correction.type === "create_ledger" && (
                            <Typography variant="body2">
                              Create ledger for {correction.transactionType} of ${correction.transactionAmount.toFixed(2)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {correction.type === "balance_update" && (
                            <Typography variant="body2" color="error">
                              ${correction.currentBalance.toFixed(2)}
                            </Typography>
                          )}
                          {correction.type === "create_ledger" && (
                            <Typography variant="body2" color="text.secondary">
                              No ledger
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {correction.type === "balance_update" && (
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              ${correction.newBalance.toFixed(2)}
                            </Typography>
                          )}
                          {correction.type === "create_ledger" && (
                            <Typography variant="body2" color="success.main">
                              Ledger will be created
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewMode(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={executeCorrections}
            disabled={loading}
          >
            {loading ? "Executing..." : "Execute Corrections"}
          </Button>
        </DialogActions>
      </Dialog>

      {loading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" sx={{ mt: 1 }}>
            {progress.toFixed(0)}% complete
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BalanceCorrectionTool;

